import { useState, useEffect, useCallback, useRef } from "react";
import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import { useWorkflow } from "../context/WorkflowContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export function usePublishData(workflow, open, onPublishSuccess, onOpenChange) {
    const { t } = useTranslation();
    const [publicUrl, setPublicUrl] = useState("");
    const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
    const [selectedPublishingTargets, setSelectedPublishingTargets] = useState(["all"]);
    const [companyGroups, setCompanyGroups] = useState([]);
    const [publicationInfo, setPublicationInfo] = useState(null);
    const [isLoadingPublicationInfo, setIsLoadingPublicationInfo] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [serviceToken, setServiceToken] = useState(null);
    const didRun = useRef(false);
    const didRefetch = useRef(false);
    const { refetchWorkflow } = useWorkflow();
    const { currentUserCount, activePlanSubscription } = useAuth();

    const hasActiveSubscription = !!activePlanSubscription;
    const hasTeamMembers = (currentUserCount || 0) > 1;

    const { call: deleteTrigger } = useBluelagoon({ method: "deleteWorkflowTrigger" });
    const { call: updateTrigger } = useBluelagoon({ method: "updateWorkflowTrigger" });
    const { call: getGroups } = useBluelagoon({ method: "getGroups" });
    const { call: publishToGroupsCall } = useBluelagoon({ method: "publishToGroups" });
    const { call: unpublishToGroupsCall } = useBluelagoon({ method: "unpublishToGroups" });
    const { call: getAppPublicationInfo } = useBluelagoon({ method: "getAppPublicationInfo" });
    const { call: generateServiceTokenCall } = useBluelagoon({ method: "generateWorkflowServiceToken" });

    const loadGroups = useCallback(async () => {
        try {
            const response = await getGroups();
            if (response.status === 200) setCompanyGroups(response.data.groups || []);
        } catch (error) {
            console.error("Error loading groups:", error);
        }
    }, [getGroups]);

    const loadPublicationInfo = useCallback(async () => {
        if (!workflow?.app?.id) return;
        setIsLoadingPublicationInfo(true);
        try {
            const response = await getAppPublicationInfo({ appId: workflow.app.id });
            if (response.status === 200) {
                setPublicationInfo(response.data);
                if (response.data.isPublishedToAll) {
                    setSelectedPublishingTargets(["all"]);
                } else if (response.data.publishedGroups.length > 0) {
                    setSelectedPublishingTargets(response.data.publishedGroups.map((g) => g.id));
                }
            }
        } catch (error) {
            console.error("Error loading publication info:", error);
        } finally {
            setIsLoadingPublicationInfo(false);
        }
    }, [getAppPublicationInfo, workflow?.app?.id]);

    const loadServiceToken = useCallback(async () => {
        if (!workflow?.id || workflow.workflowType !== "APP_V2") return;
        try {
            const response = await generateServiceTokenCall({ id: workflow.id });
            if (response.status === 200 && response.token) setServiceToken(response.token);
        } catch (error) {
            console.error("Error generating service token:", error);
        }
    }, [generateServiceTokenCall, workflow?.id, workflow?.workflowType]);

    useEffect(() => {
        if (open) {
            loadGroups();
            loadPublicationInfo();
            if (workflow?.workflowType === "APP_V2") loadServiceToken();
        }
    }, [open, loadGroups, loadPublicationInfo, loadServiceToken, workflow?.workflowType]);

    useEffect(() => {
        if (workflow && workflow.workflowTriggers.length > 0 && !didRun.current) {
            const trigger = workflow.workflowTriggers.find((tr) => tr.type === "APP");
            if (trigger) {
                setPublicUrl(Bluelagoon.appEndpoint.replace("{publicKey}", trigger.publicKey));
                didRun.current = true;
            }
        }
    }, [workflow]);

    useEffect(() => {
        if (open && !didRefetch.current) {
            didRefetch.current = true;
            refetchWorkflow();
        } else {
            didRefetch.current = false;
        }
    }, [open, refetchWorkflow]);

    const generatePublicUrl = useCallback(async () => {
        if (!hasActiveSubscription) {
            toast.error(t("publishModal.publicPublishing.requiresSubscription"));
            return;
        }
        setIsGeneratingUrl(true);
        try {
            const response = await updateTrigger({ workflowId: workflow.id, type: "APP", config: {} });
            if (response.status === 200) {
                setPublicUrl(Bluelagoon.appEndpoint.replace("{publicKey}", response.trigger.publicKey));
                toast.success(t("publishModal.publicPublishing.urlGeneratedSuccess"));
                refetchWorkflow();
                onPublishSuccess?.();
            } else {
                toast.error(t("publishModal.publicPublishing.urlGeneratedError"));
            }
        } catch (error) {
            toast.error(t("publishModal.publicPublishing.urlGeneratedError"));
        } finally {
            setIsGeneratingUrl(false);
        }
    }, [hasActiveSubscription, updateTrigger, workflow?.id, refetchWorkflow, onPublishSuccess, t]);

    const handleRemovePublicUrl = useCallback(async () => {
        const trigger = workflow.workflowTriggers.find((tr) => tr.type === "APP");
        if (!trigger) return;
        try {
            const response = await deleteTrigger({ workflowId: workflow.id, triggerId: trigger.id });
            if (response.status === 200) {
                toast.success(t("publishModal.domain.urlRemoved"));
                setPublicUrl("");
                refetchWorkflow();
                onPublishSuccess?.();
            } else {
                toast.error(t("publishModal.errors.publishError"));
            }
        } catch (error) {
            toast.error(t("publishModal.errors.publishError"));
        }
    }, [workflow, deleteTrigger, refetchWorkflow, onPublishSuccess, t]);

    const handleGroupPublish = useCallback(async () => {
        setIsPublishing(true);
        try {
            const response = await publishToGroupsCall({
                appId: workflow.app.id,
                targetGroups: selectedPublishingTargets,
            });
            if (response.status === 200) {
                toast.success(t("publishModal.groupPublishing.publishSuccess"));
                await loadPublicationInfo();
                onOpenChange(false);
                onPublishSuccess?.();
            } else {
                toast.error(response.data?.error || t("publishModal.errors.publishError"));
            }
        } catch (error) {
            toast.error(t("publishModal.errors.publishError"));
        } finally {
            setIsPublishing(false);
        }
    }, [publishToGroupsCall, workflow?.app?.id, selectedPublishingTargets, loadPublicationInfo, onOpenChange, onPublishSuccess, t]);

    const handleUnpublishGroup = useCallback(async () => {
        setIsPublishing(true);
        try {
            const response = await unpublishToGroupsCall({ appId: workflow.app.id });
            if (response.status === 200) {
                toast.success(t("publishModal.groupPublishing.unpublishSuccess"));
                await loadPublicationInfo();
                setSelectedPublishingTargets(["all"]);
                onOpenChange(false);
                onPublishSuccess?.();
            } else {
                toast.error(response.data?.error || t("publishModal.errors.unpublishError"));
            }
        } catch (error) {
            toast.error(t("publishModal.errors.unpublishError"));
        } finally {
            setIsPublishing(false);
        }
    }, [unpublishToGroupsCall, workflow?.app?.id, loadPublicationInfo, onOpenChange, onPublishSuccess, t]);

    const getEmbedUrl = useCallback((requiresLogin = true) => {
        if (workflow.workflowType === "APP_V2") {
            const displayDomain = workflow.customDomain || workflow.internalDomain;
            if (displayDomain) {
                let url = `https://${displayDomain}`;
                if (!requiresLogin && serviceToken) {
                    url += `?x_token_auth=${encodeURIComponent(serviceToken)}&embed=true`;
                } else {
                    url += `?embed=true`;
                }
                return url;
            }
        }
        return publicUrl;
    }, [workflow, serviceToken, publicUrl]);

    const getEmbedCode = useCallback((requiresLogin = true) => {
        const embedUrl = getEmbedUrl(requiresLogin);
        if (!embedUrl) return "";
        return `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
    }, [getEmbedUrl]);

    return {
        publicUrl, isGeneratingUrl,
        selectedPublishingTargets, setSelectedPublishingTargets,
        companyGroups, publicationInfo, isLoadingPublicationInfo,
        isPublishing, serviceToken, hasActiveSubscription, hasTeamMembers,
        generatePublicUrl, handleRemovePublicUrl,
        handleGroupPublish, handleUnpublishGroup,
        getEmbedUrl, getEmbedCode,
    };
}
